import React from "react";
import { useTranslation } from "react-i18next";
import type { PaginatedResponse } from "@/api/types";
import { QueryError } from "@/components/shared/query-error";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableRow } from "@/components/ui/table";
import { DividerRow, SKELETON_ROW_COUNT } from "./data-table-helpers";
import type { ColumnDef } from "./types";

interface DataTableBodyProps<T extends Record<string, unknown>> {
	data: PaginatedResponse<T> | undefined;
	columns: ColumnDef<T>[];
	loading: boolean;
	isError?: boolean;
	onRetry?: () => void;
	selectable: boolean;
	colCount: number;
	rowKeyField: string;
	selectedKeys?: Set<string>;
	onSelectKey?: (key: string) => void;
	onRowClick?: (row: T) => void;
	emptyMessage?: string;
}

export function DataTableBody<T extends Record<string, unknown>>({
	data,
	columns,
	loading,
	isError,
	onRetry,
	selectable,
	colCount,
	rowKeyField,
	selectedKeys,
	onSelectKey,
	onRowClick,
	emptyMessage,
}: DataTableBodyProps<T>) {
	const { t } = useTranslation();

	if (loading) {
		return (
			<TableBody>
				{Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
					<React.Fragment key={i}>
						<TableRow>
							{selectable && <TableCell className="w-10" />}
							{columns.map((col) => (
								<TableCell
									key={String(col.key)}
									className={
										[
											col.key === "_actions" ? "w-14 text-right" : "",
											col.className ?? "",
										]
											.filter(Boolean)
											.join(" ") || undefined
									}
								>
									<Skeleton className="h-4 w-full" />
								</TableCell>
							))}
						</TableRow>
						{i < SKELETON_ROW_COUNT - 1 && <DividerRow colSpan={colCount} />}
					</React.Fragment>
				))}
			</TableBody>
		);
	}

	if (isError) {
		return (
			<TableBody>
				<TableRow>
					<TableCell colSpan={colCount} className="py-8">
						<QueryError onRetry={onRetry} />
					</TableCell>
				</TableRow>
			</TableBody>
		);
	}

	if ((data?.results?.length ?? 0) === 0) {
		return (
			<TableBody>
				<TableRow>
					<TableCell
						colSpan={colCount}
						className="text-muted-foreground py-12 text-center"
					>
						{emptyMessage ?? t("dataTable.noData")}
					</TableCell>
				</TableRow>
			</TableBody>
		);
	}

	return (
		<TableBody>
			{(data?.results ?? []).map((row, i) => {
				const rowKey = selectable
					? String((row as Record<string, unknown>)[rowKeyField] ?? "")
					: String(i);
				const isSelected = selectable && selectedKeys!.has(rowKey);

				return (
					<React.Fragment key={rowKey}>
						<TableRow
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							className={onRowClick ? "cursor-pointer" : undefined}
						>
							{selectable && (
								<TableCell
									className="w-10"
									onClick={(e) => {
										e.stopPropagation();
										onSelectKey?.(rowKey);
									}}
								>
									<Checkbox
										checked={isSelected}
										aria-label={`${t("dataTable.selectRow")} ${rowKey}`}
									/>
								</TableCell>
							)}
							{columns.map((col) => (
								<TableCell
									key={String(col.key)}
									className={
										[
											col.key === "_actions" ? "w-14 text-right" : "",
											col.className ?? "",
										]
											.filter(Boolean)
											.join(" ") || undefined
									}
								>
									{col.render
										? col.render(row)
										: col.key === "_actions"
											? null
											: String(
													(row as Record<string, unknown>)[col.key as string] ??
														"—",
												)}
								</TableCell>
							))}
						</TableRow>
						{data?.results[i + 1] !== undefined && (
							<DividerRow colSpan={colCount} />
						)}
					</React.Fragment>
				);
			})}
		</TableBody>
	);
}
